import { RouteConfig } from "vue-router";
import { Route } from "vue-router/types/router";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import { getLoginData } from "@/lib/authHelper";
import { authoritiesRouteGuard } from "@/lib/routerGuards";
import { getProfileUrl } from "@/lib/urlHelpers";
import config from "@/config";
import { enrichRouteMeta } from "@/router/helpers";
import { profileRoutes } from "@/router/routes/profile";

const routes: RouteConfig[] = [
  {
    path: "/home",
    name: "home",
    components: {
      default: () => import("@/views/PageFeed"),
      unlockFeed: () => import(/* webpackChunkName: "profile" */ "@/views/UnlockFeed"),
      stories: () => import(/* webpackChunkName: "profile" */ "@/views/Stories"),
    },
  },
  ...(config.featureSearchBar
    ? [
        {
          path: "/search-user",
          name: "search-user",
          props: true,
          component: () => import("@/views/PageSearchUser"),
        },
      ]
    : []),
  {
    path: "/messages",
    name: "messageThreads",
    component: () => import("@/views/PageMessageThreads"),
    props: route => ({ isMassMessage: !!route.query.massMessage }),
  },
  {
    path: "/messages/mailing",
    name: "massMessage",
    component: () => import("@/views/PageMassMessage"),
    props: route => ({ ids: route.params.ids ? route.params.ids.split(",") : undefined }),
  },
  {
    path: "/messages/:userId",
    name: "messages",
    components: {
      default: () => import("@/views/PageMessages"),
      stories: () => import(/* webpackChunkName: "profile" */ "@/views/Stories"),
    },
    props: {
      default: true,
    },
  },
  {
    path: "/notifications",
    name: "notifications",
    component: () => import("@/views/PageNotifications"),
    props: true,
  },
  {
    path: "/more",
    name: "more",
    props: route => ({ section: route.query.section }),
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageMore"),
  },
  {
    path: "/more/payeeInfo",
    name: "profilePayeeInfo",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PagePayeeInfo"),
  },
  {
    path: "/more/payeeInfoEdit",
    name: "profilePayeeInfoEdit",
    component: () => import(/* webpackChunkName: "more" */ "@/views/PagePayeeInfoEdit"),
  },
  {
    path: "/more/sublevels",
    name: "profileSubscriptionLevels",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageSubscriptionLevels"),
  },
  ...(config.featureWelcomeMessage
    ? [
        {
          path: "/more/following",
          name: "followingSettings",
          component: () => import(/* webpackChunkName: "more" */ "@/views/PageFollowingSettings"),
        },
      ]
    : []),
  {
    path: "/more/cards",
    name: "profilePaymentCards",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PagePaymentCards"),
  },
  {
    path: "/more/balance",
    name: "balance",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageBalance"),
  },
  {
    path: "/more/storiesArchive",
    name: "storiesArchive",
    props: true,
    components: {
      default: () => import(/* webpackChunkName: "more" */ "@/views/PageStoriesArchive"),
      stories: () => import(/* webpackChunkName: "profile" */ "@/views/Stories"),
    },
  },
  {
    path: "/more/massMessagesStatistics",
    name: "massMessagesStatistics",
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageMassMessageStat"),
    beforeEnter(to, from, next) {
      const data = getLoginData();
      const requiredAuthorities = ["updateMyContentPrice", "getMyMassMessageStat"];
      if (checkAuthorities(data?.authorities ?? [], requiredAuthorities)) next();
      else next({ name: "more" });
    },
  },
  ...(config.featurePayoutRequest
    ? [
        {
          path: "/more/payout-requests",
          name: "payoutRequests",
          props: true,
          component: () => import(/* webpackChunkName: "more" */ "@/views/PagePayoutRequests"),
        },
      ]
    : []),
  {
    path: "/more/referrals",
    name: "referrals",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageReferrals"),
  },
  {
    path: "/more/creatorsByTalentRep",
    name: "creatorsByTalentRep",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageCreatorsByTalentRep"),
  },
  {
    path: "/more/priceSettings",
    name: "priceSettings",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PagePriceSettings"),
  },
  {
    path: "/more/recurring",
    name: "manageRecurring",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageManageRecurring"),
  },
  ...(config.featureEvents
    ? [
        {
          path: "/more/events",
          name: "events",
          component: () => import(/* webpackChunkName: "more" */ "@/views/PageEvents"),
        },
      ]
    : []),
  {
    path: "/more/lists",
    name: "manageGroups",
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageGroups"),
  },
  {
    path: "/more/lists/:groupId",
    name: "manageGroupUsers",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageGroupUsers"),
  },
  {
    path: "/more/vault",
    name: "manageVault",
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageVault"),
    beforeEnter(to, from, next) {
      const data = getLoginData();
      const requiredAuthorities = ["updateMyVault", "getMyVault"];
      if (checkAuthorities(data?.authorities ?? [], requiredAuthorities)) {
        next();
      } else {
        next({ name: "more" });
      }
    },
  },
  {
    path: "/more/emailNotificationsSettings",
    name: "emailNotificationsSettings",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageEmailNotificationsSettings"),
    beforeEnter(to, from, next) {
      const data = getLoginData();
      if (!data?.user.email) {
        next({ name: "more" });
        return;
      }
      next();
    },
  },
  ...(config.webPushNotificationsPublicKey
    ? [
        {
          path: "/more/pushNotificationsSettings",
          name: "pushNotificationsSettings",
          props: true,
          component: () => import(/* webpackChunkName: "more" */ "@/views/PagePushNotificationsSettings"),
        },
      ]
    : []),
  {
    path: "/more/account/change-password",
    name: "changePassword",
    component: () => import("@/views/PageForgotPasswordConfirmCode"),
    props: () => {
      const data = getLoginData();
      return {
        email: data?.user.email,
        changePassword: true,
      };
    },
    beforeEnter(to, from, next) {
      const data = getLoginData();
      if (!data?.user.email) {
        next({ name: "more" });
        return;
      }
      next();
    },
  },
  {
    path: "/more/account/change-password/:token",
    name: "changePasswordSet",
    component: () => import("@/views/PageForgotPasswordSet"),
    props: route => {
      const data = getLoginData();
      return {
        email: data?.user.email,
        token: route.params.token,
        changePassword: true,
      };
    },
  },
  {
    path: "/more/account/delete",
    name: "deleteAccount",
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageDeleteAccount"),
    props: true,
  },
  {
    path: "/profile",
    name: "myProfile",
    beforeEnter(to, from, next) {
      const data = getLoginData();
      if (data) next(getProfileUrl(data.user));
      else next("/");
    },
  },
  {
    path: "/profile/:userId/edit",
    name: "profilePageEdit",
    props: true,
    component: () => import(/* webpackChunkName: "profile" */ "@/views/PageProfileEdit"),
    beforeEnter(to, from, next) {
      const data = getLoginData();
      const isAuthor = data?.user.id === to.params.userId;
      const requiredAuthorities = isAuthor
        ? ["updateMyQuestionaryInfo", "updateMyPersonalInfo", "updateMyFile"]
        : ["updateAnyQuestionaryInfo", "updateAnyPersonalInfo", "updateAnyFile"];
      if (checkAuthorities(data?.authorities ?? [], requiredAuthorities)) next();
      else next(getProfileUrl(to.params.userId));
    },
  },
  ...(config.featurePrivateProfile ? profileRoutes : []),
  ...(config.featureStatements
    ? [
        {
          path: "/more/statements",
          name: "statements",
          props: true,
          component: () => import(/* webpackChunkName: "more" */ "@/views/PageStatements"),
        },
      ]
    : []),
  {
    path: "/more/purchases",
    name: "purchases",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PagePurchaseGallery"),
    beforeEnter(_, __, next) {
      authoritiesRouteGuard(["getMyPurchasedFile"]) ? next() : next({ name: "/" });
    },
  },
  {
    path: "/more/purchases/:username",
    name: "purchasesFeed",
    props: (route: Route) => ({
      onFeed: false,
      username: route.params.username,
    }),
    component: () => import(/* webpackChunkName: "more" */ "@/views/PagePurchasesGalleryFeed"),
    beforeEnter(_, __, next) {
      authoritiesRouteGuard(["getMyPurchasedFile"]) ? next() : next({ name: "/" });
    },
  },
  {
    path: "/more/purchases/:username/feed",
    name: "purchasesFeedList",
    props: (route: Route) => ({
      onFeed: true,
      username: route.params.username,
    }),
    component: () => import(/* webpackChunkName: "more" */ "@/views/PagePurchasesGalleryFeed"),
    beforeEnter(_, __, next) {
      authoritiesRouteGuard(["getMyPurchasedFile"]) ? next() : next({ name: "/" });
    },
  },
];

export default routes.map(route => enrichRouteMeta(route, { private: true }));
